import { useEffect, useState } from 'react'
import { doc, onSnapshot, setDoc, Timestamp } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import {
  Alert,
  Box,
  Button,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { db, functions } from '../../firebase'

const SETTINGS_ID = 'subscription_payment'
const INBOUND_URL =
  'https://us-central1-welamart-cef13.cloudfunctions.net/paymentInboundEvents'

export default function SubscriptionSettingsPage() {
  const [walletNumber, setWalletNumber] = useState('01055683000')
  const [priceEgp, setPriceEgp] = useState('')
  const [durationDays, setDurationDays] = useState('30')
  const [listenerToken, setListenerToken] = useState('')
  const [savingSettings, setSavingSettings] = useState(false)
  const [generatingToken, setGeneratingToken] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  })

  useEffect(() => {
    const unsub = onSnapshot(doc(db, 'settings', SETTINGS_ID), (snap) => {
      const data = snap.data() || {}
      setWalletNumber(String(data.walletNumber || '01055683000'))
      setPriceEgp(data.priceEgp != null ? String(data.priceEgp) : '')
      setDurationDays(String(data.durationDays || 30))
      setListenerToken(String(data.listenerToken || ''))
    })
    return () => unsub()
  }, [])

  const notify = (message: string, severity: typeof snackbar.severity) =>
    setSnackbar({ open: true, message, severity })

  const copy = async (value: string) => {
    await navigator.clipboard.writeText(value)
    notify('تم النسخ', 'success')
  }

  const saveSettings = async () => {
    const wallet = walletNumber.replace(/\D/g, '')
    const price = Math.round(Number(priceEgp))
    const days = Math.round(Number(durationDays) || 30)
    if (!/^01[0-9]{9}$/.test(wallet)) {
      notify('رقم المحفظة يجب أن يكون رقم مصري صحيح', 'error')
      return
    }
    if (!Number.isFinite(price) || price < 5) {
      notify('قيمة الاشتراك يجب ألا تقل عن 5 جنيه', 'error')
      return
    }
    setSavingSettings(true)
    try {
      await setDoc(
        doc(db, 'settings', SETTINGS_ID),
        {
          walletNumber: wallet,
          priceEgp: price,
          durationDays: Math.max(1, days),
          updatedAt: Timestamp.now(),
        },
        { merge: true },
      )
      notify('تم حفظ إعدادات الدفع', 'success')
    } catch {
      notify('تعذر حفظ الإعدادات', 'error')
    } finally {
      setSavingSettings(false)
    }
  }

  const generateToken = async () => {
    setGeneratingToken(true)
    try {
      const call = httpsCallable(functions, 'adminGeneratePaymentListenerToken')
      const res = await call({})
      const data = res.data as { listenerToken?: string }
      setListenerToken(data.listenerToken || '')
      notify('تم إنشاء رمز المستمع. انسخه إلى تطبيق مستمع شيرنوفا.', 'success')
    } catch {
      notify('تعذر إنشاء رمز المستمع', 'error')
    } finally {
      setGeneratingToken(false)
    }
  }

  return (
    <Stack spacing={2.5} sx={{ maxWidth: 760 }}>
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          سعر الاشتراك والمحفظة
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          أي طلب دفع جديد يستخدم هذه القيم. لا تظهر هذه الإعدادات مع جداول التجار حتى تبقى الصفحة واضحة.
        </Typography>
        <Stack spacing={2}>
          <TextField
            label="رقم استلام الحوالات"
            value={walletNumber}
            onChange={(e) => setWalletNumber(e.target.value)}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="قيمة الاشتراك الشهري (جنيه)"
              value={priceEgp}
              onChange={(e) => setPriceEgp(e.target.value)}
              type="number"
              fullWidth
            />
            <TextField
              label="المدة بالأيام"
              value={durationDays}
              onChange={(e) => setDurationDays(e.target.value)}
              type="number"
              fullWidth
            />
          </Stack>
          <Box>
            <Button variant="contained" onClick={saveSettings} disabled={savingSettings}>
              حفظ الإعدادات
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" sx={{ mb: 1 }}>
          مستمع التحويلات
        </Typography>
        <Alert severity="info" sx={{ mb: 2 }}>
          التأكيد التلقائي يحتاج هذا الرابط ورمز المستمع في تطبيق شيرنوفا على هاتف المحفظة.
        </Alert>
        <Stack spacing={2}>
          <TextField
            label="رابط كارسال للمستمع"
            value={INBOUND_URL}
            InputProps={{ readOnly: true }}
          />
          <Button variant="outlined" onClick={() => copy(INBOUND_URL)} sx={{ alignSelf: 'flex-start' }}>
            نسخ الرابط
          </Button>
          <Button variant="outlined" onClick={generateToken} disabled={generatingToken} sx={{ alignSelf: 'flex-start' }}>
            إنشاء رمز المستمع
          </Button>
          {listenerToken && (
            <>
              <TextField
                label="رمز المستمع"
                value={listenerToken}
                InputProps={{ readOnly: true }}
                multiline
              />
              <Button variant="outlined" onClick={() => copy(listenerToken)} sx={{ alignSelf: 'flex-start' }}>
                نسخ الرمز
              </Button>
            </>
          )}
        </Stack>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  )
}
